import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '../../../stores/sessionStore';
import { notificationsApi, NotificationItem } from '../notificationsApi';
import { formatSafeDateTime } from '../../../utils/dateUtils';
import { Bell, Check, CheckCheck, ExternalLink, Sparkles, AlertTriangle, ShieldAlert, UserCheck } from 'lucide-react';
import { NOTIFICATIONS_ENABLED } from '../../../services/config';

export const NotificationCenter: React.FC = () => {
  const session = useSessionStore((s: any) => s.session);
  const token = session?.access_token;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
  const popoverRef = useRef<HTMLDivElement>(null);

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const notifQuery = useQuery({
    queryKey: ['notifications', token],
    enabled: Boolean(token) && NOTIFICATIONS_ENABLED,
    queryFn: () => notificationsApi.list(token!),
    refetchInterval: NOTIFICATIONS_ENABLED ? 30000 : false,
  });

  const markAllRead = useMutation({
    mutationFn: () => notificationsApi.markAllRead(token!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markSingleRead = useMutation({
    mutationFn: (id: string) => notificationsApi.markAsRead(token!, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const items: NotificationItem[] = notifQuery.data?.items || [];
  const unreadCount = items.filter((n) => !n.is_read).length;

  const displayedItems = activeTab === 'unread'
    ? items.filter((n) => !n.is_read)
    : items;

  const handleNotificationClick = (n: NotificationItem) => {
    if (!n.is_read) {
      markSingleRead.mutate(n.id);
    }
    if (n.action_url) {
      setOpen(false);
      // Mapeia /seo para tarefas caso /seo não seja rota direta
      const target = n.action_url === '/seo' ? '/tasks' : n.action_url;
      navigate(target);
    }
  };

  return (
    <div style={{ position: 'relative' }} ref={popoverRef}>
      <button
        onClick={() => setOpen(!open)}
        className="ghost-button"
        style={{
          position: 'relative',
          padding: '8px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
        }}
        title="Histórico de Notificações e Alertas"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              minWidth: '16px',
              height: '16px',
              borderRadius: '999px',
              background: '#ef4444',
              color: 'white',
              fontSize: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              padding: '0 4px',
              boxShadow: '0 0 8px rgba(239, 68, 68, 0.6)',
              border: '2px solid var(--bg-card, #1e293b)',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="page-card"
          style={{
            position: 'absolute',
            top: '48px',
            right: '0',
            width: '390px',
            maxHeight: '560px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
            zIndex: 1000,
            boxShadow: '0 16px 40px rgba(0, 0, 0, 0.35)',
            border: '1px solid var(--border, rgba(255, 255, 255, 0.1))',
            borderRadius: '12px',
            background: 'var(--bg-card, #111827)',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid var(--border, rgba(255, 255, 255, 0.08))',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'var(--bg-card, #111827)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Bell size={16} style={{ color: 'var(--primary, #6366f1)' }} />
              <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>
                Notificações
              </h4>
            </div>

            {unreadCount > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--primary, #818cf8)',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontWeight: 600,
                  padding: '4px 8px',
                  borderRadius: '6px',
                }}
                title="Marcar todas como lidas"
              >
                <CheckCheck size={14} /> Ler todas
              </button>
            )}
          </div>

          {/* Abas: Todas vs Não Lidas para permitir Releitura Contínua */}
          <div
            style={{
              display: 'flex',
              borderBottom: '1px solid var(--border, rgba(255, 255, 255, 0.08))',
              background: 'rgba(0, 0, 0, 0.15)',
              padding: '4px 8px',
              gap: '6px',
            }}
          >
            <button
              onClick={() => setActiveTab('all')}
              style={{
                flex: 1,
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: activeTab === 'all' ? 'var(--primary, #6366f1)' : 'transparent',
                color: activeTab === 'all' ? '#ffffff' : 'var(--text-muted, #94a3b8)',
                fontSize: '12px',
                fontWeight: activeTab === 'all' ? 700 : 500,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              Todas ({items.length})
            </button>
            <button
              onClick={() => setActiveTab('unread')}
              style={{
                flex: 1,
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: activeTab === 'unread' ? 'var(--primary, #6366f1)' : 'transparent',
                color: activeTab === 'unread' ? '#ffffff' : 'var(--text-muted, #94a3b8)',
                fontSize: '12px',
                fontWeight: activeTab === 'unread' ? 700 : 500,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              Não lidas ({unreadCount})
            </button>
          </div>

          {/* Lista com scroll independente para releitura de histórico */}
          <div
            style={{
              overflowY: 'auto',
              flex: 1,
              maxHeight: '440px',
              background: 'var(--bg-card, #111827)',
            }}
          >
            {displayedItems.length === 0 ? (
              <div
                style={{
                  padding: '48px 24px',
                  textAlign: 'center',
                  color: 'var(--text-muted, #94a3b8)',
                }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                  }}
                >
                  <Check size={20} style={{ opacity: 0.6 }} />
                </div>
                <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: 500 }}>
                  {activeTab === 'unread'
                    ? 'Nenhuma notificação não lida.'
                    : 'Nenhuma notificação encontrada no histórico.'}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '0.75rem', opacity: 0.7 }}>
                  {activeTab === 'unread'
                    ? 'Todas as notificações foram lidas e estão na aba "Todas".'
                    : 'Alertas de SEO e do sistema aparecerão aqui automaticamente.'}
                </p>
              </div>
            ) : (
              displayedItems.map((n) => {
                const isSeoAlert = n.type === 'seo_alert' || n.type?.toLowerCase().includes('seo');
                const isValueTangibilization = n.type === 'value_tangibilization';
                const isLeadCaptured = n.type === 'new_lead_captured';
                const isSecurity = n.type === 'security';
                const isRead = Boolean(n.is_read);
                const messageText = n.content || n.message || '';

                // Estilo customizado por tipo de notificação
                let borderColor = 'transparent';
                let itemBackground = isRead
                  ? 'transparent'
                  : 'rgba(99, 102, 241, 0.08)';

                if (isSeoAlert) {
                  borderColor = '#f59e0b';
                  itemBackground = isRead
                    ? 'rgba(245, 158, 11, 0.03)'
                    : 'linear-gradient(135deg, rgba(245, 158, 11, 0.14) 0%, rgba(234, 88, 12, 0.06) 100%)';
                } else if (isValueTangibilization) {
                  borderColor = '#10b981';
                  itemBackground = isRead
                    ? 'rgba(16, 185, 129, 0.03)'
                    : 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(99, 102, 241, 0.05) 100%)';
                } else if (isLeadCaptured) {
                  borderColor = '#6366f1';
                  itemBackground = isRead
                    ? 'rgba(99, 102, 241, 0.03)'
                    : 'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(59, 130, 246, 0.05) 100%)';
                } else if (isSecurity) {
                  borderColor = '#ef4444';
                  itemBackground = isRead
                    ? 'rgba(239, 68, 68, 0.03)'
                    : 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.05) 100%)';
                }

                return (
                  <div
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--border, rgba(255, 255, 255, 0.06))',
                      background: itemBackground,
                      borderLeft: `3px solid ${borderColor}`,
                      cursor: n.action_url || !isRead ? 'pointer' : 'default',
                      transition: 'background 0.15s ease',
                      opacity: isRead ? 0.8 : 1,
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = isRead
                        ? 'rgba(255, 255, 255, 0.04)'
                        : 'rgba(99, 102, 241, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = itemBackground;
                    }}
                  >
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Badges de Categoria */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '6px',
                            gap: '6px',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            {isSeoAlert && (
                              <span
                                style={{
                                  fontSize: '9px',
                                  fontWeight: 800,
                                  color: '#f59e0b',
                                  background: 'rgba(245, 158, 11, 0.15)',
                                  border: '1px solid rgba(245, 158, 11, 0.3)',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  letterSpacing: '0.04em',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                <AlertTriangle size={10} /> ALERTA SEO & AUDITORIA TÉCNICA
                              </span>
                            )}

                            {isValueTangibilization && (
                              <span
                                style={{
                                  fontSize: '9px',
                                  fontWeight: 800,
                                  color: '#10b981',
                                  background: 'rgba(16, 185, 129, 0.15)',
                                  border: '1px solid rgba(16, 185, 129, 0.3)',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  letterSpacing: '0.04em',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                <Sparkles size={10} /> VALOR AGREGADO & ECONOMIA REAL
                              </span>
                            )}

                            {isLeadCaptured && (
                              <span
                                style={{
                                  fontSize: '9px',
                                  fontWeight: 800,
                                  color: '#818cf8',
                                  background: 'rgba(99, 102, 241, 0.15)',
                                  border: '1px solid rgba(99, 102, 241, 0.3)',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  letterSpacing: '0.04em',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                <UserCheck size={10} /> LEAD CAPTURADO
                              </span>
                            )}

                            {isSecurity && (
                              <span
                                style={{
                                  fontSize: '9px',
                                  fontWeight: 800,
                                  color: '#ef4444',
                                  background: 'rgba(239, 68, 68, 0.15)',
                                  border: '1px solid rgba(239, 68, 68, 0.3)',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  letterSpacing: '0.04em',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                <ShieldAlert size={10} /> SEGURANÇA
                              </span>
                            )}
                          </div>

                          {/* Indicador de Lida / Não Lida */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {!isRead ? (
                              <span
                                style={{
                                  width: '7px',
                                  height: '7px',
                                  borderRadius: '50%',
                                  background: '#3b82f6',
                                  display: 'inline-block',
                                }}
                                title="Não lida"
                              />
                            ) : (
                              <span
                                style={{
                                  fontSize: '10px',
                                  color: 'var(--text-muted, #94a3b8)',
                                  opacity: 0.6,
                                }}
                              >
                                Lida
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Título da Notificação */}
                        <p
                          style={{
                            margin: '0 0 4px',
                            fontSize: '0.86rem',
                            fontWeight: isRead ? 500 : 700,
                            color: isRead ? 'var(--text-muted, #cbd5e1)' : 'var(--text-main, #ffffff)',
                            lineHeight: 1.35,
                          }}
                        >
                          {n.title}
                        </p>

                        {/* Conteúdo / Mensagem */}
                        {messageText && (
                          <p
                            style={{
                              margin: '0 0 6px',
                              fontSize: '0.80rem',
                              color: 'var(--text-muted, #94a3b8)',
                              lineHeight: '1.45',
                              whiteSpace: 'pre-line',
                              wordBreak: 'break-word',
                            }}
                          >
                            {messageText}
                          </p>
                        )}

                        {/* Rodapé com data e link de ação */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontSize: '11px',
                            color: 'var(--text-muted, #64748b)',
                            marginTop: '4px',
                          }}
                        >
                          <span>{formatSafeDateTime(n.created_at)}</span>

                          {n.action_url && (
                            <span
                              style={{
                                color: 'var(--primary, #818cf8)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                                fontWeight: 600,
                              }}
                            >
                              Ver detalhes <ExternalLink size={11} />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
